<template>
  <div>
    <flex-layout align="center">
      <input
        type='checkbox'
        ref="inputRef"
        :checked="props.modelValue"
        :class="{ 'invalidField': !!validate(rules, props.modelValue) }"
        @click="emit('update:modelValue', !props.modelValue)"
      />
      <slot>
        <base-text class="ms-2 pt-1" :size="labelFontSize" :color="labelColor">{{ props.label }}</base-text>
      </slot>
    </flex-layout>
  </div>
</template>

<script lang="ts" setup>

import {ref, toRef, watch,} from 'vue';
import BaseText from '@/components/Texts/BaseText.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';
import { ValidatorRule } from "@/composable/ui/useForm";
import useFormField from "@/composable/ui/useFormField";
import {useValidator} from "@/composable/ui/useValidator";
const { validate } = useValidator();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  rules: {
    type: Array as () => ValidatorRule<string>[],
    default: () => [],
  },
  labelFontSize: {
    type: String as () => 'xs' |'sm' | 'md' | 'lg',
    default: 'sm',
  },
  labelColor: {
    type: String,
    default: 'primary',
  }
});
const emit = defineEmits(['update:modelValue']);

const inputRef = ref<HTMLElement>();

const form = useFormField(toRef(() => props.modelValue), toRef(() => props.rules));

</script>

<style scoped>
/* Basic styling */
input {
  width: 20px;
  height: 20px;
  color: var(--alternativeBlue);
  vertical-align: middle;
  -webkit-appearance: none;
  border: none;
  outline: 0;
  flex-grow: 0;
  border-radius: 2px;
  transition: background 300ms;
  cursor: pointer;
}

/* Pseudo element for check styling */
input::before {
  content: "";
  color: transparent;
  display: block;
  width: inherit;
  height: inherit;
  border-radius: inherit;
  border: 0;
  background-color: transparent;
  background-size: contain;
  box-shadow: inset 0 0 0 1px #CCD3D8;

}

/* Checked */

input:checked {
  background-color: var(--alternativeBlue);
}

input:checked::before {
  box-shadow: none;
  background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2071%2066%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M58.6138%200.97337L69.8404%2010.9715L25.8817%2064.6825L1%2039.3615L12.0556%2028.4977L25.3788%2041.3427L58.6138%200.97337Z%22%20fill%3D%22white%22/%3E%3Cpath%20d%3D%22M25.3788%2041.3427C51.9668%209.04724%2066.0982%207.63878%2069.8404%2010.9715M25.3788%2041.3427L58.6138%200.97337L69.8404%2010.9715M25.3788%2041.3427L12.0556%2028.4977L1%2039.3615L25.8817%2064.6825L69.8404%2010.9715%22%20stroke%3D%22white%22/%3E%3C/svg%3E");
  background-size: 16px 16px;
  background-position: center;
  background-repeat: no-repeat;
}

/* Disabled */
input:disabled {
  background-color: var(--primaryLight);
  opacity: 0.84;
  cursor: not-allowed;
}

/* IE */

input::-ms-check {
  content: "";
  color: transparent;
  display: block;
  width: inherit;
  height: inherit;
  border-radius: inherit;
  border: 0;
  background-color: var(--primary);
  background-size: contain;
  box-shadow: inset 0 0 0 1px #CCD3D8;
}

input:checked::-ms-check {
  box-shadow: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E %3Cpath d='M15.88 8.29L10 14.17l-1.88-1.88a.996.996 0 1 0-1.41 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7a.996.996 0 0 0 0-1.41c-.39-.39-1.03-.39-1.42 0z' fill='%23fff'/%3E %3C/svg%3E");
}

</style>
